/**
 * The referral ask and the share prompt on both thank-you pages (spec sections
 * 10 and 17).
 *
 * Three things in here are load bearing:
 *
 * 1. **The owner question is the spec's question, word for word:** "Who is one
 *    horse professional you would recommend to another horse owner?" One
 *    question, one required field. Everything else is optional, because this
 *    step must never feel like a second form standing between someone and the
 *    confirmation they already earned.
 * 2. **"We do not contact them" is stated before the field, not after.** Naming
 *    a professional is a small act of trust in a community where everyone knows
 *    everyone. The honest answer to "so will you email my farrier?" is no, and
 *    it belongs where it is read rather than in a privacy page (spec section
 *    17).
 * 3. **The share message is sent by the person, not by the site.** It is copy
 *    the referrer can read in full before they send it, addressed from them,
 *    with no send button anywhere near it. The site has no mechanism to
 *    transmit it and must not grow one.
 *
 * No counts, no launch date, no claim about how many providers have joined
 * (CLAUDE.md hard rule 1).
 */

/** Where a shared message points. 301s to /providers, query string preserved. */
export const SHARE_URL = 'https://joinherdbook.com';

export interface ReferralFieldCopy {
  label: string;
  hint?: string;
}

export interface ReferralAudienceCopy {
  eyebrow: string;
  /** The question itself. Rendered as the section heading. */
  headline: string;
  body: string;
  submitLabel: string;
  /** Replaces the form once the recommendation is in. */
  success: {
    headline: string;
    body: string;
  };
  share: {
    headline: string;
    body: string;
    /** The prefilled text, exactly as it is copied. */
    message: string;
  };
}

const privacyNote =
  'Nothing is ever sent to the person you name. Recommendations come to me, and I reach out myself, or not at all.';

const shareBody =
  "If you'd rather tell them yourself, here's a short message you can copy and send however you normally would. You send it. I never do.";

export const referralContent = {
  common: {
    privacyNote,
    /** Sits above the fields, in the same place as the signup forms'. */
    requiredNote: 'Only the name is needed. Everything else is optional.',
    fields: {
      recommendedName: {
        label: 'Their name or business name',
        hint: "Whatever you'd call them. A person, a farm, a business, all fine.",
      },
      categoryText: {
        label: 'What they do',
        hint: 'Farrier, vet, hauler, trainer, bodyworker, barn. In your own words.',
      },
      locationText: {
        label: 'Roughly where they work',
        hint: 'A town, a county, or the area they cover. Close enough is close enough.',
      },
      websiteOrContact: {
        label: "How I'd find them",
        hint: 'A website, a phone number, a Facebook page, or "ask at the barn". Whatever you have.',
      },
      note: {
        label: 'Anything I should know',
        hint: 'Why you recommend them, or anything that would help me reach out well.',
      },
    } satisfies Record<string, ReferralFieldCopy>,
    /** Failure states. The confirmation above this section is unaffected. */
    errorSummaryTitle: "That recommendation didn't send yet.",
    networkError:
      "That didn't reach us. It might be the connection. Check it and try the button again.",
    serverError: 'Something went wrong on our end. Try the button again in a moment.',
    rateLimitedError:
      "That's more submissions than we expected from one connection. Wait a few minutes and try again.",
    unmappedFieldError: "Something in that recommendation wasn't accepted. Check the fields above.",
    /** Reassurance under the failure, because the signup itself is safe. */
    failureReassurance:
      "Your early access spot isn't affected. That part's already done, and this step is optional.",
    share: {
      messageLabel: 'The message',
      copyLabel: 'Copy the message',
      copiedStatus: 'Copied. Send it whenever you like.',
      /**
       * Shown when the browser refuses clipboard access. The message is on the
       * page either way, so this is a nudge rather than an error.
       */
      manualCopyStatus: "Select the message above and copy it. This browser wouldn't do it for us.",
    },
  },

  owner: {
    eyebrow: 'One optional question',
    /** Spec section 10. Approved copy. */
    headline: 'Who is one horse professional you would recommend to another horse owner?',
    body: "A farrier, a vet, a hauler, a trainer, a bodyworker. Anyone you'd send another owner to. Naming one person is the most useful thing you can do for this list today.",
    submitLabel: 'Send this recommendation',
    success: {
      headline: "Thank you. That's exactly what I needed.",
      body: "It's on my list to follow up on by hand. I won't mention your name to them, and I won't send them anything on your behalf.",
    },
    share: {
      headline: 'Want to tell them yourself?',
      body: shareBody,
      message: [
        'I put my name on the early access list for My Herd Book, a Maryland network for connecting horse owners with the people who look after their horses.',
        "They're signing up providers now. If you want a spot, here's the page. " + SHARE_URL,
      ].join('\n\n'),
    },
  } satisfies ReferralAudienceCopy,

  provider: {
    eyebrow: 'One optional question',
    headline: 'Know somebody else who belongs in this?',
    body: "Someone you'd be glad to be listed alongside. Send me a name and I'll take it from there.",
    submitLabel: 'Send this recommendation',
    success: {
      headline: "Thank you. I'll take it from here.",
      body: "It's on my list to follow up on by hand. I won't mention your name to them, and I won't send them anything on your behalf.",
    },
    share: {
      headline: 'Want to tell them yourself?',
      body: shareBody,
      message: [
        'I reserved my spot on My Herd Book, a Maryland network for connecting horse owners with the people who take care of their horses.',
        "They're signing up providers now. If you want a spot, here's the page. " + SHARE_URL,
      ].join('\n\n'),
    },
  } satisfies ReferralAudienceCopy,
} as const;

export type ReferralContent = typeof referralContent;
